/**
 * "Manage Google Ads campaigns" page (C-fix3). Real data only — KPIs, the
 * new-campaign diff banner, the monitored table (with Remove), and the
 * connection card (Change account / Reconnect / Disconnect with confirmation).
 * Mounts the same GadsFlow used by onboarding for connect / change / edit.
 */
import { useTranslation } from '@silospot/i18n/react';
import { toast } from '@silospot/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Loader2, RefreshCw } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { GadsFlow, StatusBadge, channelLabel, money, num } from '../../components/cld/gads-flow';
import { Btn, Card, CardHead, Kpi, KpiGrid, PageHeader } from '../../components/cld/kit';
import { api, serverError } from '../../lib/api';
import { useSiteAdAccounts } from '../../lib/use-site-ad-accounts';
import { relTime } from '../../lib/watched-audit';

export function GadsManagePage(): ReactElement {
  const { t, i18n } = useTranslation('pages');
  const lang = i18n.language || 'fr';
  const qc = useQueryClient();
  const { projectId, connected, accounts, loading } = useSiteAdAccounts();
  const account = accounts[0] ?? null;
  // The picker title shows the SITE (domain), never the project UUID.
  const projectQ = useQuery({
    queryKey: ['cld-settings-project', projectId],
    queryFn: () => api.getProject(projectId as string),
    enabled: !!projectId,
  });
  const siteDomain = projectQ.data?.project.primaryDomain.replace(/^https?:\/\//, '') ?? '';
  const [editing, setEditing] = useState(false);
  const [discAsk, setDiscAsk] = useState(false);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['gads-watched'] });
    void qc.invalidateQueries({ queryKey: ['gads-site-accounts'] });
    void qc.invalidateQueries({ queryKey: ['onboarding'] });
  };

  const watchedQ = useQuery({
    queryKey: ['gads-watched', projectId, account?.id],
    queryFn: () => api.googleAdsWatched(projectId as string, account?.id as string),
    enabled: !!projectId && !!account && !editing,
  });

  const syncNow = useMutation({
    mutationFn: () => api.googleAdsSyncNow(projectId as string, account?.id as string),
    onSuccess: () => {
      toast.success(t('gadsm.syncDone'));
      refresh();
    },
    onError: (e) => toast.error(serverError(e) ?? t('gads.errGeneric')),
  });
  const removeOne = useMutation({
    mutationFn: (campaignId: string) =>
      api.googleAdsRemoveWatched(projectId as string, account?.id as string, campaignId),
    onSuccess: () => {
      toast.info(t('gadsm.removed'));
      refresh();
    },
  });
  const watchAll = useMutation({
    mutationFn: (ids: string[]) =>
      api.googleAdsSetWatched(projectId as string, {
        customerId: account?.id as string,
        campaignIds: ids,
        periodDays: (watchedQ.data?.periodDays as 30 | 90) ?? 90,
        autoSync: watchedQ.data?.autoSync ?? true,
      }),
    onSuccess: () => {
      toast.success(t('gadsm.allWatched'));
      refresh();
    },
  });
  const connect = useMutation({
    mutationFn: () => api.googleAdsConnect(window.location.pathname),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (e) => toast.error(serverError(e) ?? t('gads.toastError')),
  });
  const disconnect = useMutation({
    mutationFn: () => api.googleAdsDisconnect(),
    onSuccess: () => {
      setDiscAsk(false);
      toast.info(t('gadsm.disconnected'));
      refresh();
    },
  });

  if (loading) {
    return (
      <div className="grid place-items-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  // Not connected → connect prompt.
  if (!connected) {
    return (
      <div>
        <PageHeader title={t('gadsm.title')} sub={t('gadsm.connectSub')} />
        <Card>
          <CardHead title={t('gads.connect')} sub={t('gads.readonly')} />
          <Btn primary disabled={connect.isPending} onClick={() => connect.mutate()}>
            {connect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('gads.connect')}
          </Btn>
        </Card>
      </div>
    );
  }

  // No linked account yet, or editing → the selection flow.
  if (!account || editing) {
    return (
      <div>
        <PageHeader title={t('gadsm.connectTitle')} sub={t('gadsm.connectSub')} />
        <Card>
          <GadsFlow
            projectId={projectId as string}
            projectName={siteDomain || t('gadsm.thisSite')}
            initial={
              editing && account && watchedQ.data
                ? {
                    customerId: account.id,
                    name: account.name,
                    campaignIds: watchedQ.data.watched.map((c) => c.id),
                    periodDays: (watchedQ.data.periodDays as 30 | 90) ?? 90,
                    autoSync: watchedQ.data.autoSync,
                  }
                : null
            }
            onDone={() => {
              setEditing(false);
              refresh();
            }}
            onBack={editing ? () => setEditing(false) : undefined}
          />
        </Card>
      </div>
    );
  }

  const state = watchedQ.data;
  const currency = state?.currencyCode || 'EUR';
  const watched = state?.watched ?? [];
  const fresh = state?.fresh ?? [];

  return (
    <div>
      <PageHeader
        title={t('gadsm.title')}
        sub={t('gadsm.sub')}
        actions={
          <>
            <Btn icon={RefreshCw} disabled={syncNow.isPending} onClick={() => syncNow.mutate()}>
              {t('gadsm.syncNow')}
            </Btn>
            <Btn primary onClick={() => setEditing(true)}>
              {t('gadsm.manage')}
            </Btn>
          </>
        }
      />

      <KpiGrid cols={4}>
        <Kpi
          label={t('gadsm.kpiAccount')}
          value={<span className="text-[15px]">{account.name}</span>}
          sub={account.id}
        />
        <Kpi
          label={t('gadsm.kpiWatched')}
          value={watched.length}
          sub={t('gadsm.ofAvailable', { count: state?.availableCount ?? 0 })}
        />
        <Kpi
          label={t('gadsm.kpiLastSync')}
          value={<span className="text-[15px]">{relTime(state?.lastSyncAt ?? null, lang)}</span>}
          sub={state?.autoSync ? t('gadsm.auto') : t('gadsm.manual')}
        />
        <Kpi
          label={t('gadsm.kpiPeriod')}
          value={
            <span className="text-[15px]">
              {t('gadsf.lastNDays', { n: state?.periodDays ?? 90 })}
            </span>
          }
          sub={t('gadsm.initialHistory')}
        />
      </KpiGrid>

      {fresh.length > 0 ? (
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-info-fg/25 bg-info-bg px-4 py-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-surface text-info-fg">
            <Bell className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] font-bold text-text">
              {t('gadsm.freshTitle', { count: fresh.length })}
            </div>
            <div className="truncate text-[12.5px] text-text-2">
              {fresh.map((c) => c.name).join(' · ')}
            </div>
          </div>
          <Btn
            primary
            disabled={watchAll.isPending}
            onClick={() =>
              watchAll.mutate([...watched.map((c) => c.id), ...fresh.map((c) => c.id)])
            }
          >
            {t('gadsm.watchAll')}
          </Btn>
        </div>
      ) : null}

      <Card className="mt-4 p-0">
        <div className="p-[18px] pb-1">
          <CardHead title={t('gadsm.watchedTitle')} sub={t('gadsm.watchedSub')} />
        </div>
        <div className="overflow-x-auto px-1.5 pb-1.5">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.04em] text-text-3">
                <th className="px-2.5 py-2 text-left">{t('gadsf.colCampaign')}</th>
                <th className="px-2.5 py-2 text-left">{t('gadsf.colStatus')}</th>
                <th className="px-2.5 py-2 text-left">{t('gadsf.colType')}</th>
                <th className="px-2.5 py-2 text-right">{t('gadsf.colClicks')}</th>
                <th className="px-2.5 py-2 text-right">{t('gadsf.colSpend')}</th>
                <th className="px-2.5 py-2 text-right">{t('gadsf.colConvGoogle')}</th>
                <th className="px-2.5 py-2 text-right">{t('gadsf.colConvSilospot')}</th>
                <th className="px-2.5 py-2" />
              </tr>
            </thead>
            <tbody>
              {watched.map((c) => (
                <tr key={c.id} className="border-t border-[#f1f3f6] hover:bg-[#fafbfc]">
                  <td className="px-2.5 py-2.5">
                    <div className="font-semibold text-text">{c.name}</div>
                    <div className="font-mono text-[11px] text-text-3">{c.id}</div>
                  </td>
                  <td className="px-2.5 py-2.5">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-2.5 py-2.5 text-[12.5px] text-text-2">
                    {channelLabel(c.channelType)}
                  </td>
                  <td className="px-2.5 py-2.5 text-right font-mono text-[12px] text-text-2">
                    {num(c.clicks, lang)}
                  </td>
                  <td className="px-2.5 py-2.5 text-right font-mono text-[12px] text-text-2">
                    {money(c.costMicros, currency, lang)}
                  </td>
                  <td className="px-2.5 py-2.5 text-right font-mono text-[12px] text-text-3">
                    {num(c.convGoogle, lang)}
                  </td>
                  <td className="px-2.5 py-2.5 text-right font-mono text-[12px] font-semibold text-text">
                    {c.convVerified == null ? (
                      <span className="text-text-3">{t('gadsf.toConfigure')}</span>
                    ) : (
                      num(c.convVerified, lang)
                    )}
                  </td>
                  <td className="px-2.5 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => removeOne.mutate(c.id)}
                      className="text-[12.5px] font-semibold text-primary hover:underline"
                    >
                      {t('gadsm.remove')}
                    </button>
                  </td>
                </tr>
              ))}
              {watched.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-2.5 py-6 text-center text-[13px] text-text-3">
                    {t('gadsm.noneWatched')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="mt-4">
        <CardHead title={t('gadsm.connTitle')} sub={t('gadsm.connSub')} />
        <div className="flex flex-wrap gap-2">
          <Btn onClick={() => setEditing(true)}>{t('gadsm.changeAccount')}</Btn>
          <Btn disabled={connect.isPending} onClick={() => connect.mutate()}>
            {t('gadsm.reconnect')}
          </Btn>
          <Btn danger onClick={() => setDiscAsk(true)}>
            {t('gadsm.disconnectAll')}
          </Btn>
        </div>
      </Card>

      {discAsk ? (
        <div
          className="fixed inset-0 z-[120] grid place-items-center bg-[rgba(15,21,32,0.55)] p-4"
          onClick={() => setDiscAsk(false)}
          onKeyDown={(e) => e.key === 'Escape' && setDiscAsk(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-[420px] rounded-2xl bg-surface p-6 shadow-[0_24px_64px_rgba(16,24,40,0.28)]"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            <h2 className="text-[18px] font-bold text-text">{t('gadsm.discTitle')}</h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-text-2">{t('gadsm.discBody')}</p>
            <div className="mt-5 flex justify-end gap-2">
              <Btn onClick={() => setDiscAsk(false)}>{t('gadsm.cancel')}</Btn>
              <Btn danger disabled={disconnect.isPending} onClick={() => disconnect.mutate()}>
                {t('gadsm.disconnectAll')}
              </Btn>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
